import { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { http } from '@/lib/api';
import { CheckCircle2, XCircle, Loader2, Mail } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

/**
 * /verify-email?token=... — one-click email verification landing page.
 * Shows spinner while POSTing, then success or error state with a resend option.
 */
export default function VerifyEmail() {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const nav = useNavigate();
  const token = params.get('token') || '';
  const [state, setState] = useState('checking'); // 'checking' | 'ok' | 'error'
  const [message, setMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!token) { setState('error'); setMessage('This verification link is missing a token.'); return; }
    (async () => {
      try {
        await http.post('/auth/verify-email', { token });
        setState('ok');
        setMessage('Your email is verified. All Brotherhood+ features are unlocked.');
        try { await refresh(); } catch (err) {
          // Non-fatal: verification already succeeded server-side; local
          // user-state refresh is a nicety. Log but don't surface to user.
          console.warn('[verify-email] auth refresh failed after verify:', err);
        }
      } catch (e) {
        setState('error');
        const d = e?.response?.data?.detail;
        setMessage(typeof d === 'string' ? d : 'This verification link is invalid or has expired.');
      }
    })();
  }, [token, refresh]);

  const doResend = async (e) => {
    e.preventDefault();
    if (!resendEmail) return;
    setResending(true);
    try {
      await http.post('/auth/resend-verification', { email: resendEmail });
      setMessage(`If ${resendEmail} needs verification, a fresh link has been sent.`);
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setMessage(typeof d === 'string' ? d : "Couldn't resend right now. Try again shortly.");
    } finally { setResending(false); }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center p-6" data-testid="verify-email-page">
      <div className="w-full max-w-md bg-white rounded-3xl p-8 card-soft">
        <p className="text-[11px] uppercase tracking-[0.24em] text-gold font-semibold">Brotherhood+</p>
        <h1 className="font-heading text-3xl mt-2 mb-4">Verify your email</h1>

        {state === 'checking' && (
          <div className="flex items-center gap-3 text-navy/70" data-testid="verify-checking">
            <Loader2 className="animate-spin" size={20}/> Checking your link…
          </div>
        )}
        {state === 'ok' && (
          <div data-testid="verify-success">
            <div className="flex items-center gap-3 text-emerald-700">
              <CheckCircle2 size={22}/> <span className="font-medium">You&apos;re verified.</span>
            </div>
            <p className="mt-3 text-sm text-navy/65 leading-relaxed">{message}</p>
            <button
              onClick={() => nav('/app')}
              className="mt-6 w-full rounded-full bg-navy text-white py-3 font-medium hover:bg-navy/90 transition-smooth"
              data-testid="verify-continue"
            >
              Continue to Brotherhood+
            </button>
          </div>
        )}
        {state === 'error' && (
          <div data-testid="verify-error">
            <div className="flex items-center gap-3 text-red-700">
              <XCircle size={22}/> <span className="font-medium">Link invalid or expired.</span>
            </div>
            <p className="mt-3 text-sm text-navy/65 leading-relaxed">{message}</p>

            <form onSubmit={doResend} className="mt-6 space-y-3">
              <label className="text-xs uppercase tracking-widest text-navy/60 flex items-center gap-1.5">
                <Mail size={12}/> Resend link
              </label>
              <input
                type="email" required value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full bg-[#F9F8F6] border border-navy/10 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gold"
                data-testid="verify-resend-email"
              />
              <button
                type="submit" disabled={resending}
                className="w-full rounded-full bg-navy text-white py-3 font-medium hover:bg-navy/90 transition-smooth disabled:opacity-50"
                data-testid="verify-resend-submit"
              >
                {resending ? 'Sending…' : 'Send a new verification link'}
              </button>
            </form>
          </div>
        )}

        <div className="mt-6 pt-6 border-t border-navy/5 text-center">
          <Link to="/login" className="text-sm text-navy/60 hover:text-navy">← Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
